import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeClose } from '@/components/ui/useEscapeClose';
import { useFocusTrap } from '@/components/ui/useFocusTrap';
import { useNavigate } from 'react-router-dom';
import { api } from '@/shared/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { IconButton } from '@/components/ui/IconButton';
import { DeltaChip } from '@/components/ui/DeltaChip';
import { IconDownload, IconX } from '@/components/ui/Icon';
import { downloadCsv, fmtK, KpiDetail, Filters } from './format';
import { DetailTable } from './tables';

// ---------------------------------------------------------------------------
// Clickable KPI tile
// ---------------------------------------------------------------------------
// One detail panel per Analytics tab, and one tab shows at a time.
export const KPI_PANEL_ID = 'bi-kpi-panel';

export function KpiButton({ onClick, active = false, children }: { onClick: () => void; active?: boolean; children: React.ReactNode }) {
  // h-full + auto-rows-fr on the grid keeps every tile the same size; the
  // [&>div]:h-full forces the KpiTile root to fill the equalized cell.
  return (
    <button type="button" onClick={onClick} aria-expanded={active} aria-controls={active ? KPI_PANEL_ID : undefined}
      className="block text-left h-full w-full transition hover:-translate-y-0.5 hover:shadow-md rounded-[12px] focus-ring [&>div]:h-full">
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Reusable inline KPI-expand (shared by every tab). Clicking a KPI tile toggles
// an inline detail panel beneath the KPI row — title + count badge + CSV export
// + close — instead of a modal. Mirrors the priority-agent dashboard.
// ---------------------------------------------------------------------------
export function useKpiView() {
  const [kpiView, setKpiView] = useState<string | null>(null);
  return {
    kpiView,
    toggleKpi: (k: string) => setKpiView((v) => (v === k ? null : k)),
    closeKpi: () => setKpiView(null),
  };
}

export function InlineKpiPanel({ detail, onClose, year }: { detail: KpiDetail | null; onClose: () => void; year: number }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shown = detail?.title ?? null;
  useEffect(() => {
    if (!shown) return undefined;
    // The tile that opened the panel is the one that names it (aria-controls).
    const opener = document.querySelector(`[aria-controls="${KPI_PANEL_ID}"]`);
    headingRef.current?.focus({ preventScroll: true });
    return () => {
      // Closed from inside (Close took focus with it): back to that tile. A
      // tap on another tile or on the same one keeps the focus it gave.
      const active = document.activeElement;
      if ((!active || active === document.body) && opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [shown]);
  if (!detail) return null;
  return (
    <Card id={KPI_PANEL_ID} padding="lg">
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 ref={headingRef} tabIndex={-1} className="text-[0.8125rem] font-bold text-primary-text">{detail.title}</h3>
          <span className="text-[0.6875rem] font-semibold rounded-lg px-2 py-0.5" style={{ color: detail.color, background: `${detail.color}1a` }}>{detail.rows.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <ExportBtn onClick={() => downloadCsv(`${detail.title.replace(/[^a-z0-9]+/gi, '_')}-${year}.csv`, detail.headers, detail.rows)} />
          <IconButton label="Close" onClick={onClose}><IconX size={13} /></IconButton>
        </div>
      </div>
      <DetailTable cols={detail.headers} rows={detail.rows} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// FcKpiCard — the exact FinComp horizontal KPI card.
// ~42px circular icon box on the left, 11px uppercase letter-spaced label,
// 18px bold value, optional sub, right-aligned delta with up/down arrow.
// Fills with the accent color when active. (src: shared/KPICard.jsx)
// ---------------------------------------------------------------------------
export type FcKpiCardProps = {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  accent: string;            // hex, e.g. FC_COLORS.green
  active?: boolean;
  delta?: { value: number; favorable?: boolean; context?: string };  // omit to hide
  onClick?: () => void;
  style?: React.CSSProperties;
};
export function FcKpiCard({ label, value, sub, icon, accent, active, delta, onClick, style }: FcKpiCardProps) {
  const rootStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        borderRadius: 20,
        border: active ? `1.5px solid ${accent}` : '1px solid var(--border,#e2e8f0)',
        background: active ? accent : 'var(--surface,#fff)',
        // Match the shared KpiTile (Summary tab) padding so all BI KPIs are the
        // same fixed size.
        padding: '6px 10px',
        boxShadow: active ? '0 4px 12px rgba(0,0,0,0.1)' : '0 1px 2px rgba(15,23,42,0.04),0 1px 3px rgba(15,23,42,0.03)',
        transition: 'transform 0.2s,box-shadow 0.2s',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        ...style,
  };
  const inner = (
    <>
      {/* Left: icon + text */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 12, flexShrink: 0,
          background: active ? 'rgba(255,255,255,0.2)' : '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: active ? 'rgba(255,255,255,0.9)' : 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: active ? '#fff' : '#0f172a', lineHeight: 1.2, marginTop: 1, letterSpacing: '-0.02em' }}>{value}</div>
          {sub != null && sub !== '' && <div style={{ fontSize: 11, color: active ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
        </div>
      </div>

      {/* Right: delta badge */}
      {delta != null && <DeltaChip value={delta.value} favorable={delta.favorable} context={delta.context} onAccent={active} className="shrink-0" />}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} aria-expanded={!!active} aria-controls={active ? KPI_PANEL_ID : undefined}
      className="focus-ring" style={{ ...rootStyle, width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit' }}>
      {inner}
    </button>
  ) : (
    <div style={rootStyle}>{inner}</div>
  );
}

// Helper: build the 17px stroked-SVG icon FinComp uses inside FcKpiCard.
export function fcKpiIcon(children: React.ReactNode, color = '#64748b') {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// FcKpiRow — lays out 6 KPI cards in one responsive row, exactly like FinComp
// (repeat(2,1fr) on mobile, repeat(6,1fr) otherwise; gap 8). Pass exactly six
// FcKpiCard children. (src: CustomerOrdersTab.jsx grid)
// ---------------------------------------------------------------------------
export function FcKpiRow({ children, mobile, style }: { children: React.ReactNode; mobile?: boolean; style?: React.CSSProperties }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: mobile ? 'repeat(2,1fr)' : 'repeat(6,1fr)',
      gridAutoRows: '1fr',
      // Match the Summary KPI row's gap (gap-1 = 4px) so tile widths match.
      gap: 4,
      ...style,
    }}>
      {children}
    </div>
  );
}

export function ExportBtn({ onClick }: { onClick: () => void }) {
  return <IconButton label="Download CSV" onClick={onClick}><IconDownload size={13} /></IconButton>;
}
export function Empty({ hint = 'Try another year or period in the filters.', compact = false }: { hint?: string; compact?: boolean }) {
  if (compact) {
    return (
      <div className="text-center">
        <div className="text-[0.75rem] text-secondary">No data for this period.</div>
        <div className="text-meta text-muted">{hint}</div>
      </div>
    );
  }
  return <EmptyState title="No data for this period." description={hint} />;
}

export function Failed({ onRetry, compact = false }: { onRetry?: () => void; compact?: boolean }) {
  if (compact) {
    // Fits a Home widget body (often 120-150px): one line and a small button.
    return (
      <div role="alert" className="text-center">
        <div className="text-[0.75rem] text-danger">Could not load this data.</div>
        {onRetry && <Button size="sm" variant="ghost" onClick={onRetry} className="mt-1">Try again</Button>}
      </div>
    );
  }
  return (
    <Card>
      <EmptyState role="alert" title="Could not load this data." description="Check the connection, then try again."
        action={onRetry ? <Button size="sm" variant="secondary" onClick={onRetry}>Try again</Button> : undefined} />
    </Card>
  );
}

// Placeholders that hold the layout while a chart or a tab loads (charts-10).
export function ChartLoading({ height = 188 }: { height?: number | string }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading chart" style={{ height }} className="flex p-1">
      <Skeleton className="h-full w-full" />
    </div>
  );
}

export function TabLoading() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="space-y-1">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-1">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-[52px] rounded-card" />)}
      </div>
      <div className="grid gap-1 lg:grid-cols-2">
        <Skeleton className="h-[220px] rounded-card" />
        <Skeleton className="h-[220px] rounded-card" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Balance Sheet / Reports link tab
// ---------------------------------------------------------------------------
export function LinkTab({ title, to, desc }: { title: string; to: string; desc: string }) {
  const nav = useNavigate();
  return (
    <Card padding="lg">
      <div className="py-8 text-center space-y-3">
        <div className="text-[0.9375rem] font-semibold text-primary-text">{title}</div>
        <p className="text-[0.8125rem] text-secondary max-w-md mx-auto">{desc}</p>
        <Button onClick={() => nav(to)}>Open {title} →</Button>
      </div>
    </Card>
  );
}

// ===========================================================================
// Transaction drill-down — clicking a customer / product / vendor anywhere on
// the board opens a modal listing the ACTUAL underlying transaction lines
// (posted AR-invoice lines, or PO lines), never the edit form. Faithful port
// of FinComp's ExploreModal (/tmp/pa/src/components/ExploreModal.jsx): a
// centered card with a title, a scrollable transaction table (Invoice#/Date/
// Customer/Product/Qty/Amount/Agent), a CSV export and a Close button.
// ===========================================================================

// Server shape returned by GET /api/v1/bi/drill (already unwrapped from { data }).
export type DrillResponse = {
  title: string;
  columns: string[];
  rows: Array<Record<string, string | number | null>>;
  currency: string;
};

// Human labels for the known server column keys (the modal falls back to the
// raw key for anything unmapped).
const DRILL_COL_LABELS: Record<string, string> = {
  invoice_number: 'Invoice #',
  po_number: 'PO #',
  date: 'Date',
  customer_name: 'Customer',
  vendor_name: 'Vendor',
  product: 'Product',
  qty: 'Qty',
  amount: 'Amount',
  agent_name: 'Agent',
};
const DRILL_NUMERIC = new Set(['qty', 'amount']);

function drillCell(key: string, v: string | number | null): string {
  if (v == null) return '';
  if (key === 'date') return String(v).slice(0, 10);
  if (DRILL_NUMERIC.has(key)) return fmtK(Number(v));
  return String(v);
}

export type DrillState = {
  title: string;
  loading: boolean;
  data: DrillResponse | null;
};

export function DrillModal({ state, onClose }: { state: DrillState | null; onClose: () => void }) {
  useEscapeClose(!!state, onClose);
  const panelRef = useRef<HTMLDivElement>(null);
  // Focus in on open, Tab wraps inside, focus back to the trigger on close.
  useFocusTrap(panelRef, { active: !!state });
  if (!state) return null;
  const cols = state.data?.columns ?? [];
  const rows = state.data?.rows ?? [];
  const totals = state.data
    ? {
        qty: rows.reduce((s, r) => s + Number(r.qty ?? 0), 0),
        amount: rows.reduce((s, r) => s + Number(r.amount ?? 0), 0),
      }
    : { qty: 0, amount: 0 };
  const exportCsv = () => {
    if (!state.data) return;
    downloadCsv(
      `${state.title.replace(/[^a-z0-9]+/gi, '_')}.csv`,
      cols.map((c) => DRILL_COL_LABELS[c] ?? c),
      rows.map((r) => cols.map((c) => (r[c] == null ? '' : c === 'date' ? String(r[c]).slice(0, 10) : (r[c] as string | number)))),
    );
  };

  // Portaled to <html> like Modal, so the dialog paints above the AI Companion panel.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="drill-modal-title"
      aria-describedby="drill-modal-desc"
      onClick={onClose}
      className="fixed inset-0 z-modal flex items-center justify-center p-5"
      style={{ background: 'rgba(0,15,40,0.6)', backdropFilter: 'blur(8px)' }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-[90%] max-w-[900px] max-h-[85vh] rounded-[12px] border border-border bg-surface"
        style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
          <div>
            <div id="drill-modal-title" className="text-[0.9375rem] font-bold text-primary-text">{state.title}</div>
            <div id="drill-modal-desc" className="text-meta text-muted mt-0.5">
              {state.data ? `${rows.length} rows` : 'Loading…'}
              {state.data?.currency ? ` · ${state.data.currency}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {state.data && rows.length > 0 && <ExportBtn onClick={exportCsv} />}
            <IconButton label="Close" onClick={onClose}><IconX size={13} /></IconButton>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 pb-4">
          {state.loading && <div className="text-center py-10 text-secondary text-[0.8125rem]">Loading…</div>}
          {!state.loading && state.data && rows.length === 0 && (
            <div className="text-center py-10 text-muted text-[0.8125rem]">No transactions found.</div>
          )}
          {!state.loading && state.data && rows.length > 0 && (
            <table className="w-full text-[0.75rem] mt-2" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className="sticky top-0 bg-surface" style={{ borderBottom: '2px solid var(--border-light,#f0f4f8)' }}>
                  {cols.map((c) => (
                    <th
                      key={c}
                      className={`py-2 px-1.5 font-semibold text-secondary ${DRILL_NUMERIC.has(c) ? 'text-right' : 'text-left'}`}
                    >
                      {DRILL_COL_LABELS[c] ?? c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light,#f8fafc)' }}>
                    {cols.map((c) => (
                      <td
                        key={c}
                        className={`py-1.5 px-1.5 ${
                          DRILL_NUMERIC.has(c)
                            ? 'text-right font-numeric tabular-nums'
                            : 'truncate max-w-[180px] text-primary-text'
                        }`}
                      >
                        {drillCell(c, r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border,#e2e8f0)' }}>
                  {cols.map((c, i) => {
                    if (c === 'qty') return <td key={c} className="py-2 px-1.5 text-right font-numeric tabular-nums font-bold">{fmtK(totals.qty)}</td>;
                    if (c === 'amount') return <td key={c} className="py-2 px-1.5 text-right font-numeric tabular-nums font-bold">{fmtK(totals.amount)}</td>;
                    if (i === 0) return <td key={c} className="py-2 px-1.5 font-bold text-primary-text">Total</td>;
                    return <td key={c} />;
                  })}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.documentElement,
  );
}

// ---------------------------------------------------------------------------
// DrillProvider + useDrill — context that lets any BI tab open the transaction
// drill modal. openDrill builds the /bi/drill query from the provider's active
// period (year + quarter[] + month[]) plus the customer/product/vendor passed
// by the caller, fetches the rows, and shows the modal.
// ---------------------------------------------------------------------------
export type OpenDrillArgs = {
  title: string;
  customer?: string;
  product?: string;
  vendor?: string;
  agent?: string;
  source?: 'sales' | 'purchase';
};

type DrillContextValue = { openDrill: (args: OpenDrillArgs) => void };
const DrillContext = createContext<DrillContextValue | null>(null);

export function useDrill(): DrillContextValue {
  const ctx = useContext(DrillContext);
  if (!ctx) throw new Error('useDrill must be used within a <DrillProvider>');
  return ctx;
}

export function DrillProvider({ filters, children }: { filters: Filters; children: React.ReactNode }) {
  const [state, setState] = useState<DrillState | null>(null);

  const openDrill = useCallback(
    (args: OpenDrillArgs) => {
      const source: 'sales' | 'purchase' = args.source ?? 'sales';
      setState({ title: args.title, loading: true, data: null });
      const query: Record<string, string | number | undefined> = {
        source,
        year: filters.year,
        quarter: filters.quarter.length ? filters.quarter.join(',') : undefined,
        month: filters.month.length ? filters.month.join(',') : undefined,
        customer: args.customer || undefined,
        product: args.product || undefined,
        vendor: args.vendor || undefined,
        agent: args.agent || undefined,
      };
      // api.get prepends /api/v1 and unwraps { data } → DrillResponse.
      void api
        .get<DrillResponse>('/bi/drill', query)
        .then((data) => setState((prev) => (prev ? { ...prev, loading: false, data } : prev)))
        .catch(() =>
          setState((prev) =>
            prev ? { ...prev, loading: false, data: { title: prev.title, columns: [], rows: [], currency: '' } } : prev,
          ),
        );
    },
    [filters],
  );

  return (
    <DrillContext.Provider value={{ openDrill }}>
      {children}
      <DrillModal state={state} onClose={() => setState(null)} />
    </DrillContext.Provider>
  );
}
